export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ORG_ADMIN: 'ORG_ADMIN',
  SITE_ENGINEER: 'SITE_ENGINEER',
  PROJECT_MANAGER: 'PROJECT_MANAGER',
  CLUSTER_HEAD: 'CLUSTER_HEAD',
  VIEWER: 'VIEWER',
} as const;

/** Human-readable labels for each canonical role key. */
export const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ORG_ADMIN: 'Organization Admin',
  SITE_ENGINEER: 'Site Engineer',
  PROJECT_MANAGER: 'Project Manager',
  CLUSTER_HEAD: 'Cluster Head',
  VIEWER: 'Viewer',
};

/**
 * Normalize a role display name or canonical identifier to a canonical role key.
 * This MUST stay in sync with apps/worker/src/middleware/auth.ts normalizeRole.
 */
export function normalizeRole(role: string): string {
  const map: Record<string, string> = {
    "admin": "ORG_ADMIN",
    "org_admin": "ORG_ADMIN",
    "org admin": "ORG_ADMIN",
    "organization admin": "ORG_ADMIN",
    "manager": "PROJECT_MANAGER",
    "project_manager": "PROJECT_MANAGER",
    "project manager": "PROJECT_MANAGER",
    "engineer": "SITE_ENGINEER",
    "site_engineer": "SITE_ENGINEER",
    "site engineer": "SITE_ENGINEER",
    "cluster head": "CLUSTER_HEAD",
    "cluster_head": "CLUSTER_HEAD",
    "viewer": "VIEWER",
    "super_admin": "SUPER_ADMIN",
    "super admin": "SUPER_ADMIN",
  };
  return map[role.toLowerCase().trim()] || role.toUpperCase().replace(/\s+/g, "_");
}

export function normalizeRoles(roles: string[]): string[] {
  return roles.map(normalizeRole);
}

export function hasNormalizedRole(roles: string[], target: string): boolean {
  const normalized = normalizeRoles(roles);
  return normalized.includes(normalizeRole(target));
}

export function hasAnyNormalizedRole(roles: string[], targets: string[]): boolean {
  const normalized = normalizeRoles(roles);
  return targets.some(t => normalized.includes(normalizeRole(t)));
}

export const hasRole = hasNormalizedRole;
export const hasAnyRole = hasAnyNormalizedRole;

/**
 * Permission keys reserved for the canonical Manager / Cluster Head approval
 * workflow. They can never be granted to custom roles, and on canonical roles
 * they are mandatory and fixed. MUST stay in sync with
 * apps/worker/src/middleware/permissions.ts PROTECTED_WORKFLOW_PERMISSIONS.
 */
export const PROTECTED_WORKFLOW_PERMISSIONS = [
  "requirement:manager_approve",
  "requirement:manager_reject",
  "requirement:cluster_approve",
  "requirement:cluster_reject",
  "actual:manager_approve",
  "actual:manager_reject",
  "actual:cluster_approve",
  "actual:cluster_reject",
] as const;

/**
 * Simplified user-facing access levels per module.
 *
 * Each access level maps to one or more granular backend permission keys.
 * The RoleFormPage renders a single checkbox per access level, and when
 * toggled it adds/removes the underlying granular keys in the form state.
 *
 * Modules and levels are rendered in declaration order.
 */
export interface AccessLevel {
  label: string;
  description: string;
  permissions: string[];
}

export const ACCESS_LEVELS: Record<string, AccessLevel[]> = {
  Dashboard: [
    { label: 'View', description: 'View the organization dashboard', permissions: ['dashboard:view'] },
  ],
  'Departmental Labour-Requirement': [
    { label: 'View', description: 'View Departmental Labour-Requirement in scope', permissions: ['requirement:view'] },
    { label: 'Manage', description: 'Create, edit and submit Departmental Labour-Requirement', permissions: ['requirement:create', 'requirement:edit', 'requirement:submit'] },
    { label: 'Approve', description: 'Approve or reject Departmental Labour-Requirement', permissions: ['requirement:manager_approve', 'requirement:manager_reject', 'requirement:cluster_approve', 'requirement:cluster_reject'] },
  ],
  'Departmental Labour -Actual': [
    { label: 'View', description: 'View Departmental Labour -Actual records in scope', permissions: ['actual:view'] },
    { label: 'Manage', description: 'Create, edit and submit Departmental Labour -Actual records', permissions: ['actual:create', 'actual:edit', 'actual:submit'] },
    { label: 'Approve', description: 'Approve or reject Departmental Labour -Actual records', permissions: ['actual:manager_approve', 'actual:manager_reject', 'actual:cluster_approve', 'actual:cluster_reject'] },
  ],
  'Rate Card': [
    { label: 'View', description: 'View and download the organization rate card', permissions: ['rate_cards:view'] },
  ],
  Reports: [
    { label: 'View', description: 'View reports', permissions: ['reports:view'] },
    { label: 'Download', description: 'Export reports (CSV)', permissions: ['reports:export'] },
  ],
  Masters: [
    { label: 'View', description: 'View projects, blocks, activities, units, worker categories and settings', permissions: ['projects:view', 'blocks:view', 'activities:view', 'units:view', 'worker_categories:view', 'settings:view', 'audit:view'] },
    { label: 'Manage', description: 'Create and edit projects, blocks, activities, units, worker categories and settings', permissions: ['projects:manage', 'blocks:manage', 'activities:manage', 'units:manage', 'worker_categories:manage', 'settings:manage'] },
    { label: 'Delete', description: 'Permanently delete unused projects, blocks, activities, units and worker categories', permissions: ['projects:delete', 'blocks:delete', 'activities:delete', 'units:delete', 'worker_categories:delete'] },
  ],
};

export const permissions = {
  canCreateOperationalRecord: (roles: string[]) =>
    hasNormalizedRole(roles, ROLES.SITE_ENGINEER),
  canEditOperationalRecord: (
    roles: string[],
    record: { status: string; createdBy?: string },
    userId: string | undefined
  ) =>
    hasNormalizedRole(roles, ROLES.SITE_ENGINEER) &&
    (record.status === "DRAFT" || record.status === "REJECTED_BY_MANAGER" || record.status === "REJECTED_BY_CLUSTER_HEAD") &&
    record.createdBy === userId,

  canDeleteOperationalRecord: (
    roles: string[],
    record: { status: string; createdBy?: string },
    userId: string | undefined
  ) =>
    hasNormalizedRole(roles, ROLES.SITE_ENGINEER) &&
    record.status === "DRAFT" &&
    record.createdBy === userId,

  canSubmitOperationalRecord: (
    roles: string[],
    record: { status: string; createdBy?: string },
    userId: string | undefined
  ) =>
    hasNormalizedRole(roles, ROLES.SITE_ENGINEER) &&
    record.status === "DRAFT" &&
    record.createdBy === userId,

  canManagerDecide: (roles: string[]) =>
    hasNormalizedRole(roles, ROLES.PROJECT_MANAGER),

  canClusterDecide: (roles: string[]) =>
    hasNormalizedRole(roles, ROLES.CLUSTER_HEAD),
};

/**
 * Stable signature of the caller's effective permissions, included in React
 * Query keys so cached dashboard data is isolated whenever a user's grants
 * change (login, logout, role edit, permission edit).
 */
export function permissionSignature(permissions: string[] = []): string {
  return [...permissions].sort().join(',');
}

/**
 * Org-scope bypass for the dashboard: ORG_ADMIN (tenant admin) or a platform
 * SUPER_ADMIN acting in explicit support mode. Mirrors the worker's isOrgAdmin
 * so the web can scope admin-only rendering decisions.
 */
export function isOrgAdmin(user: { roles: string[]; scope?: 'tenant' | 'platform'; support?: unknown } | null | undefined): boolean {
  if (!user) return false;
  return hasNormalizedRole(user.roles, ROLES.ORG_ADMIN) || (user.scope === 'platform' && !!user.support);
}
